import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UtensilsCrossed, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getPostLoginRoute } from "@/lib/permissions";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { rememberUiPack } from "@/lib/uiPackStorage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { t } = useTranslation();
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("admin@restaurant.local");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [needs2fa, setNeeds2fa] = useState(false);
  const [loginToken, setLoginToken] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const from = location.state?.from;

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (needs2fa) {
        const loggedIn = await verifyTwoFactor(loginToken, otpCode.trim());
        rememberUiPack(loggedIn?.company?.ui_pack);
        toast.success(t("common.welcomeBack"));
        const target = getPostLoginRoute(loggedIn?.permissions, from, loggedIn);
        navigate(target, { replace: true });
        return;
      }
      const result = await login(email.trim(), password);
      if (result?.requires_2fa) {
        setNeeds2fa(true);
        setLoginToken(result.login_token);
        toast.info("Enter the 6-digit code from your authenticator app");
        return;
      }
      rememberUiPack(result?.company?.ui_pack);
      toast.success(t("common.welcomeBack"));
      const target = getPostLoginRoute(result?.permissions, from, result);
      navigate(target, { replace: true });
    } catch (err) {
      const message =
        err.response?.data?.errors?.email?.[0] ||
        err.response?.data?.message ||
        "Login failed. Check your credentials.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen grid place-items-center bg-brand-cream p-4"
      data-testid="login-page"
    >
      <div className="w-full max-w-[380px]">
        <div className="flex justify-end mb-2">
          <LanguageSwitcher />
        </div>
        <div className="text-center mb-5">
          <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand-orange text-white mb-3">
            <UtensilsCrossed size={22} />
          </div>
          <h1 className="text-[18px] font-semibold text-brand-brown">{t("app.title")}</h1>
          <p className="text-[12px] text-slate-500 mt-1">{t("app.signInSubtitle")}</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-brand-cream-border rounded-xl p-5 shadow-sm space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-[12px]">
              {t("common.email")}
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-9 text-[13px]"
              required
              data-testid="login-email"
            />
          </div>

          {!needs2fa ? (
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[12px]">
                {t("common.password")}
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-9 text-[13px]"
                required
                data-testid="login-password"
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="otp" className="text-[12px]">
                Authentication code
              </Label>
              <Input
                id="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="h-9 text-[13px] font-mono tracking-widest"
                placeholder="000000"
                required
                data-testid="login-otp"
              />
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-9 text-[13px]"
            disabled={submitting}
            data-testid="login-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.signingIn")}
              </>
            ) : needs2fa ? (
              "Verify code"
            ) : (
              t("common.signIn")
            )}
          </Button>
        </form>

        <p className="text-[11px] text-slate-400 text-center mt-3">
          Default: admin@restaurant.local / admin123
        </p>
      </div>
    </div>
  );
}
